import { Link } from '@tanstack/react-router';
import { TriangleAlertIcon } from 'lucide-react';

import { useWorkflowGaps } from '../hooks/useWorkflowGaps';
import { missingPartsLabel } from '../missingParts';

/**
 * A board whose workflow has lost an agent or a skill says so, before anybody
 * creates a task on it (spec 107 US2).
 *
 * The reporter's own words: "I dont know how it breaks the workflow, how to
 * fix." Nothing downstream answers either — the worker mirrors whatever sits in
 * `.skills/` and treats an absent folder as a silent no-op, so a run produces
 * worse work and reports nothing. The board is the last place to say it, and
 * the only one somebody about to start work is looking at.
 *
 * A warning, never a bar. The run will happen and will not fail; the agent
 * simply works without what is gone, and that is a judgement for the person
 * starting it rather than for this component.
 *
 * Absent whenever nothing is missing, which is almost always, so an ordinary
 * board is exactly as it was.
 */
export function MissingPartsNotice({
  workspaceId,
  workflowKey,
}: {
  workspaceId: string;
  workflowKey: string;
}) {
  const gone = useWorkflowGaps(workspaceId, workflowKey);
  if (gone.length === 0) return null;

  return (
    <div
      role="status"
      className="border-state-warning/30 bg-fill-warning/15 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border px-3 py-2 text-sm"
    >
      <TriangleAlertIcon
        aria-hidden="true"
        className="text-state-warning size-4 shrink-0"
        strokeWidth={1.75}
      />
      <span className="text-state-warning font-medium">
        {missingPartsLabel(gone.length)}
      </span>
      <span className="text-muted-foreground min-w-0">
        This workflow names {gone.map((dep) => dep.key).join(', ')}, which this
        workspace no longer has. Tasks will still run, without
        {gone.length === 1 ? ' it' : ' them'}.
      </span>
      <Link
        to="/workspaces/$workspaceId/library"
        params={{ workspaceId }}
        search={{
          tab: 'workflow' as const,
          q: workflowKey,
          state: 'all' as const,
        }}
        className="text-foreground ms-auto shrink-0 underline underline-offset-2 hover:no-underline"
      >
        Restore in the Library
      </Link>
    </div>
  );
}
